// AON-08 — the always-on space's status, one store for every surface.
//
// The page, the settings blocks, the switcher row, the consent dialog and the
// move dialog's reason hook all read the space through this store, and
// through `knownSpace(load)` — never through `load.status === 'ready'`, because
// a failed re-read keeps the last known space (K9/D71).
//
// Import direction (no cycles): alwaysOnAutoMount.ts → this file →
// alwaysOnMountStore.ts. This file never imports alwaysOnAutoMount.ts or
// workspaceStore.ts; workspaceStore imports THIS file (logout calls reset()).
// Code under features/settings, features/encryption and features/documents
// imports this file by path, never through the alwayson barrel.
//
// Generations (R2-C1). Every read and every write takes a new generation; a
// read's result is written only while its generation is still the current
// one. So:
//   - `refresh()` joins the in-flight read only when nothing superseded it;
//   - `revalidateAfterMutation()` never joins: it starts a new read, which
//     supersedes every read that started before the mutation answered;
//   - `setStatus` / `setNone` / `reset` supersede every in-flight read;
//   - the auto-mount publishes its own status read through
//     `publishRead(token, …)`, which writes only if nothing was written or read
//     since `readToken()` — so a read that started before a switch-on cannot
//     overwrite the switch-on's result.
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import type { AlwaysOnBannerState, AlwaysOnLatestAccess } from '@/components/ui/AlwaysOnBanner';
import { type AlwaysOnSpaceStatus, getAlwaysOnAccessLog, getAlwaysOnSpace } from '@/features/workspaces/api';

import { recordAlwaysOnMount, useAlwaysOnMountStore } from '../alwaysOnMountStore';

export type SpaceLoad =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'ready'; space: AlwaysOnSpaceStatus }
  /** The last read failed. `space` is the last value known in this session, if any. */
  | { status: 'error'; space?: AlwaysOnSpaceStatus };

/** What a status read reports to the caller that awaited it. `'ok'`: the store
 *  now holds a confirmed value (this read's, or a newer write's). `'failed'`:
 *  it does not; the store is `error` (keeping any earlier space). */
export type SpaceReadResult = 'ok' | 'failed';

export interface AlwaysOnSpaceState {
  load: SpaceLoad;
  latestAccess: AlwaysOnLatestAccess;
  /**
   * R2-C1: the read after a REJECTED mutation failed, so whether the space is
   * on is not known. While true, no surface may assert a key state ("only your
   * devices can open this", "it is still on"): read the banner state through
   * `selectSpaceBannerState`. Cleared by the next successful read, a
   * `setStatus` / `setNone`, or `reset`.
   */
  unconfirmed: boolean;
  /** The user the store was filled for; '' when unbound (fresh, or after reset). */
  userId: string;
  actions: {
    /** Revalidate: every consuming surface calls it on mount, whatever the
     *  status. `loading` only from `idle`; otherwise the current value stays on
     *  screen while the read runs. Joins an in-flight read that nothing has
     *  superseded. A throw or a 15 s timeout → `error`, keeping the space. */
    refresh: () => Promise<SpaceReadResult>;
    /**
     * The read after a mutation was REJECTED (create, switch-on, switch-off).
     * The rejection may be a lost response to a committed mutation, so the
     * caller states what this read shows, never what the store held before.
     * Never joins an in-flight read: it starts a new one, which supersedes every
     * older read. Resolves `'ok'` when the store holds a confirmed value — read
     * `knownSpace(load)` then — and `'failed'` when it does not; then the store
     * is `unconfirmed` and the caller shows `…error.unconfirmed` with a "Check
     * status" action that calls this again. Never rejects.
     */
    revalidateAfterMutation: () => Promise<SpaceReadResult>;
    /**
     * A mutation (create, switch-on, switch-off) TIMED OUT client-side
     * (`isAlwaysOnCallTimeout`). The request was aborted here, but the server
     * may still commit it, so a read now could land first and show the
     * pre-mutation state. So no read: the store goes `unconfirmed` (the load
     * goes `error`, keeping any known space), every in-flight read is
     * superseded, and the caller offers "Check status", which calls
     * `revalidateAfterMutation`.
     */
    markUnconfirmed: () => void;
    /** Store a status a mutation returned (or another authoritative read).
     *  Supersedes every in-flight read. */
    setStatus: (space: AlwaysOnSpaceStatus) => void;
    /** The user has no space (404). Supersedes every in-flight read. */
    setNone: () => void;
    /** The generation now. Capture it BEFORE starting a status read of your own,
     *  and hand it to `publishRead` with the result. */
    readToken: () => number;
    /** Writes a read started at `token` (a space → ready, null → none) only if
     *  nothing was written or read since. Returns whether it wrote. */
    publishRead: (token: number, space: AlwaysOnSpaceStatus | null) => boolean;
    /** Binds the store to a user. A store filled for ANOTHER user is reset
     *  first; an unbound store adopts the user and keeps what it holds. */
    bindUser: (userId: string) => void;
    refreshLatestAccess: () => Promise<void>;
    reset: () => void;
  };
}

const READ_TIMEOUT_MS = 15_000;
/** The bound on every always-on call that goes through a bare `apiFetch`, which
 *  has no timeout of its own: the pre-flight policy read, the devices list,
 *  create, switch-on and switch-off. */
const ALWAYS_ON_CALL_TIMEOUT_MS = 15_000;
const LATEST_TIMEOUT_MS = 10_000;
const LATEST_FRESH_MS = 60_000;

// Module state, not store state: none of it renders.
let generation = 0;
let inFlight: { gen: number; promise: Promise<SpaceReadResult> } | null = null;
let latestGen = 0;
let latestFetchedAt = 0;

/** The space of `ready`, or of `error` when it kept one. */
export function knownSpace(load: SpaceLoad): AlwaysOnSpaceStatus | undefined {
  if (load.status === 'ready') return load.space;
  if (load.status === 'error') return load.space;
  return undefined;
}

/** Fail closed on the state string: only the exact `state === 'on'` is on, and
 *  only the exact `state === 'off'` is off. Any other value is not a state this
 *  client knows, so it is `unconfirmed` — never `off`, whose banner claims
 *  "only your devices can open this". */
export function selectBannerState(space: AlwaysOnSpaceStatus): AlwaysOnBannerState {
  if (space.state !== 'on' && space.state !== 'off') return { kind: 'unconfirmed' };
  if (space.state === 'on') {
    const s = space.suspended;
    if (s) {
      // The server folds "the org policy could not be read" into org_policy
      // with no orgs, so that combination is not a confirmed reason.
      if (s.reason === 'org_policy' && !(s.orgs && s.orgs.length > 0)) return { kind: 'paused', reason: 'unknown' };
      if (s.reason === 'kill_switch' || s.reason === 'org_policy' || s.reason === 'plan') {
        return { kind: 'paused', reason: s.reason };
      }
      return { kind: 'paused', reason: 'unknown' };
    }
    return { kind: 'on' };
  }
  return {
    kind: 'off',
    keyDestructionPending: space.key_destruction_pending === true,
    everOn: Boolean(space.consent_version),
  };
}

/**
 * The banner / status-row state for the store as a whole (R2-C1): `unconfirmed`
 * after a rejected mutation whose re-read failed, else `selectBannerState` of
 * the known space; undefined when no space is known.
 */
export function selectSpaceBannerState(
  s: Pick<AlwaysOnSpaceState, 'load' | 'unconfirmed'>,
): AlwaysOnBannerState | undefined {
  if (s.unconfirmed) return { kind: 'unconfirmed' };
  const space = knownSpace(s.load);
  return space ? selectBannerState(space) : undefined;
}

type Exists = 'yes' | 'no' | 'unknown';

function existsOf(load: SpaceLoad): Exists {
  if (knownSpace(load)) return 'yes';
  if (load.status === 'none') return 'no';
  return 'unknown';
}

/** Keeps `usable` in the mount store in step with every confirmed status, when
 *  that workspace is the one mounted (R2-I2). */
function syncMount(space: AlwaysOnSpaceStatus): void {
  const m = useAlwaysOnMountStore.getState();
  if (!m.mounted || m.workspaceId !== space.workspace_id) return;
  recordAlwaysOnMount(space.workspace_id, space.state === 'on' && !space.suspended, space.name ?? '');
}

/** What `boundedCall` (and the status read) reject with when the CLIENT gave
 *  up. Not a server answer: a timed-out mutation may still commit. */
class AlwaysOnCallTimeout extends Error {
  constructor(ms: number) {
    super(`always-on call timed out after ${ms} ms`);
    this.name = 'AlwaysOnCallTimeout';
  }
}

/** True when `e` is a client-side timeout, not a server refusal. After a
 *  MUTATING call, a timeout means "not known": call `markUnconfirmed()`. */
export function isAlwaysOnCallTimeout(e: unknown): boolean {
  return e instanceof AlwaysOnCallTimeout;
}

/** Rejects when `p` has not settled within `ms`, and aborts `ac` then. Hand the
 *  call `ac.signal`, or the request runs on after the timeout and only the wait
 *  stops (`boundedCall` does both). */
function withTimeout<T>(p: Promise<T>, ms: number, ac?: AbortController): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      ac?.abort();
      reject(new AlwaysOnCallTimeout(ms));
    }, ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

/** Runs `call` with a fresh AbortController's signal and bounds it at
 *  ALWAYS_ON_CALL_TIMEOUT_MS: on timeout the request is aborted and the
 *  promise rejects with an error `isAlwaysOnCallTimeout` recognises. For the
 *  bare-apiFetch always-on calls, which have no timeout of their own. A
 *  MUTATING caller must not read a timeout as a refusal: see `markUnconfirmed`. */
export function boundedCall<T>(call: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const ac = new AbortController();
  return withTimeout(call(ac.signal), ALWAYS_ON_CALL_TIMEOUT_MS, ac);
}

export const useAlwaysOnSpaceStore = create<AlwaysOnSpaceState>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => {
        function writeConfirmed(space: AlwaysOnSpaceStatus | null): void {
          set((s) => {
            s.load = space ? { status: 'ready', space } : { status: 'none' };
            s.unconfirmed = false;
          });
          if (space) syncMount(space);
        }

        /** What a superseded read reports: the newer in-flight read's result
         *  if there is one (it is current, so it is not the caller), else
         *  whether the store now holds a confirmed value. */
        function settleSuperseded(): Promise<SpaceReadResult> | SpaceReadResult {
          const cur = inFlight;
          if (cur && cur.gen === generation) return cur.promise;
          const st = get().load.status;
          return st === 'ready' || st === 'none' ? 'ok' : 'failed';
        }

        function startRead(afterMutation: boolean): Promise<SpaceReadResult> {
          const gen = ++generation;
          if (get().load.status === 'idle') set((s) => void (s.load = { status: 'loading' }));
          const ac = new AbortController();
          const self: Promise<SpaceReadResult> = (async (): Promise<SpaceReadResult> => {
            try {
              const space = await withTimeout(getAlwaysOnSpace({ signal: ac.signal }), READ_TIMEOUT_MS, ac);
              if (gen !== generation) return settleSuperseded();
              writeConfirmed(space);
              return 'ok';
            } catch {
              if (gen !== generation) return settleSuperseded();
              set((s) => {
                const kept = knownSpace(s.load);
                s.load = kept ? { status: 'error', space: kept } : { status: 'error' };
                if (afterMutation) s.unconfirmed = true;
              });
              return 'failed';
            } finally {
              if (inFlight?.gen === gen) inFlight = null;
            }
          })();
          inFlight = { gen, promise: self };
          return self;
        }

        return {
          load: { status: 'idle' },
          latestAccess: { status: 'idle' },
          unconfirmed: false,
          userId: '',
          actions: {
            refresh: () => {
              if (inFlight && inFlight.gen === generation) return inFlight.promise;
              return startRead(false);
            },
            revalidateAfterMutation: () => startRead(true),
            markUnconfirmed: () => {
              generation++;
              inFlight = null;
              set((s) => {
                const kept = knownSpace(s.load);
                s.load = kept ? { status: 'error', space: kept } : { status: 'error' };
                s.unconfirmed = true;
              });
            },
            setStatus: (space) => {
              generation++;
              writeConfirmed(space);
            },
            setNone: () => {
              generation++;
              writeConfirmed(null);
            },
            readToken: () => generation,
            publishRead: (token, space) => {
              if (token !== generation) return false;
              generation++;
              writeConfirmed(space);
              return true;
            },
            bindUser: (userId) => {
              const bound = get().userId;
              if (bound === userId) return;
              if (bound !== '') get().actions.reset();
              set((s) => void (s.userId = userId));
            },
            refreshLatestAccess: async () => {
              if (!knownSpace(get().load)) return;
              if (!useAlwaysOnMountStore.getState().mounted) {
                set((s) => void (s.latestAccess = { status: 'unavailable' }));
                return;
              }
              if (latestFetchedAt && Date.now() - latestFetchedAt < LATEST_FRESH_MS) return;
              const gen = ++latestGen;
              const ac = new AbortController();
              const timer = setTimeout(() => ac.abort(), LATEST_TIMEOUT_MS);
              if (get().latestAccess.status === 'idle') set((s) => void (s.latestAccess = { status: 'loading' }));
              // ceiling: latest access is read from a 20-row log page and costs a keyd call; a server-side latest read if the banner must be exact or cheap
              try {
                const page = await getAlwaysOnAccessLog({ limit: 20, signal: ac.signal });
                if (gen !== latestGen) return;
                latestFetchedAt = Date.now();
                const open = page.entries.find((e) => e.kind === 'open');
                set((s) => {
                  s.latestAccess = open
                    ? { status: 'ready', at: open.at, label: open.label ?? '', purposeKind: open.purpose_kind ?? '' }
                    : { status: 'none' };
                });
              } catch {
                if (gen !== latestGen) return;
                set((s) => void (s.latestAccess = { status: 'unavailable' }));
              } finally {
                clearTimeout(timer);
              }
            },
            reset: () => {
              generation++;
              inFlight = null;
              latestGen++;
              latestFetchedAt = 0;
              set((s) => {
                s.load = { status: 'idle' };
                s.latestAccess = { status: 'idle' };
                s.unconfirmed = false;
                s.userId = '';
              });
            },
          },
        };
      }),
    ),
    { name: 'AlwaysOnSpaceStore' },
  ),
);

/** 'yes' when a space is known (also through a failed re-read), 'no' only on a
 *  confirmed 404, 'unknown' otherwise — so one failed read never removes the
 *  settings tab, the switcher row or the off switch (D71). */
export function useAlwaysOnSpaceExists(): Exists {
  return useAlwaysOnSpaceStore((s) => existsOf(s.load));
}

/** Non-reactive `useAlwaysOnSpaceExists` for registries and non-React code. */
export function alwaysOnSpaceExists(): Exists {
  return existsOf(useAlwaysOnSpaceStore.getState().load);
}
